import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {ActivitiesGroup} from '../../backend/interfaces/activities-group';
import {Modal, ModalProps} from '../modal/modal';
import {Stack, SxProps, Typography} from '@mui/material';
import {CarePlan} from '../../backend/interfaces/care-plan';
import FlatInput from '../flat-input/flat-input';
import {useEffect, useState} from 'react';
import CollapsibleCheckbox from '../daily-care-log/filters-drawer/collapsible-checkbox';
import './careplan-modal.scss';
import {getActivityNameByActivitySectorId} from '../../utils/functions/get-activity-name-by-section';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {EntityTemplate} from '../../backend/responses/entity-template';
import {convertArrayToIndex} from '../../utils/functions';
import NestedCheckbox from '../checkbox/nested-checkbox';

export type TemplateAction = 'save' | 'import';

interface TemplateModalProps extends Omit<ModalProps, 'actions'> {
  open: boolean;
  entry?: CarePlan | null;
  allActivitiesGroup?: Array<ActivitiesGroup>;
  submitting?: TemplateAction;
  action: TemplateAction;
  templates: EntityTemplate[];
  onClose: () => void;
  onSave: (name: string, description: string, activitiesGroupsIds: string[]) => void;
  onImport: (name: string, templateId: string, activitiesGroupsIds: string[]) => void;
}
export const MAX_TEMPLATE_NAME_CHARACTERS = 35;
export const MAX_TEMPLATE_DESCRIPTION_CHARACTERS = 120;

export const TemplatesModal = ({
  open,
  entry,
  allActivitiesGroup,
  submitting,
  action,
  templates,
  onClose,
  onSave,
  onImport,
}: TemplateModalProps) => {
  const {t} = useTranslation();
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [activitiesExpanded, setActivitiesExpanded] = useState<boolean>(true);
  const [activitiesIds, setActivitiesIds] = useState<string[]>([]);
  const [activities, setActivities] = useState<ActivitiesGroup[]>([]);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [templatesIndex] = useState<{[id: string]: EntityTemplate}>(
    convertArrayToIndex(templates, 'id')
  );
  const [selectedTemplate, setSelectedTemplate] = useState<EntityTemplate>();
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [confirmation, setConfirmation] = useState<string>('');
  const [nameError, setNameError] = useState<string | undefined>();
  const [descriptionError, setDescriptionError] = useState<string | undefined>();
  const collapsibleCheckboxSx: SxProps = {
    background: 'var(--dcm-bgseniormenu)',
    borderRadius: '5px',
    marginTop: '4px',
  };

  useEffect(() => {
    if (name.trim().length > 0 && name.trim().length > MAX_TEMPLATE_NAME_CHARACTERS) {
      setNameError(
        t(translations.activities_type_entry.templates.name_error, {
          max: MAX_TEMPLATE_NAME_CHARACTERS,
        })
      );
    } else {
      setNameError(undefined);
    }
  }, [name, t]);

  useEffect(() => {
    if (
      description.trim().length > 0 &&
      description.trim().length > MAX_TEMPLATE_DESCRIPTION_CHARACTERS
    ) {
      setDescriptionError(
        t(translations.activities_type_entry.templates.description_error, {
          max: MAX_TEMPLATE_DESCRIPTION_CHARACTERS,
        })
      );
    } else {
      setDescriptionError(undefined);
    }
  }, [description, t]);

  useEffect(() => {
    if (!entry?.activitiesGroups || !allActivitiesGroup || action !== 'save') return;
    const ids = entry.activitiesGroups.map((group) => group.activitiesGroupId);
    setActivitiesIds(ids);
    setCheckedIds(ids);
    setActivities(allActivitiesGroup?.filter((group) => ids?.includes(group.id)) ?? []);
  }, [allActivitiesGroup, action, entry?.activitiesGroups]);

  useEffect(() => {
    if (!selectedTemplate?.activitiesGroups || !selectedTemplate) return;
    const ids = selectedTemplate.activitiesGroups.map((group) => group.activitiesGroupId);
    setActivitiesIds(ids);
    setCheckedIds(ids);
    setActivities(allActivitiesGroup?.filter((group) => ids?.includes(group.id)) ?? []);
  }, [allActivitiesGroup, selectedTemplate]);

  const activityName = getActivityNameByActivitySectorId(entry?.activitiesSectorId);
  const willReplaceActivities =
    entry?.activitiesGroups?.some((group) => checkedIds.includes(group.activitiesGroupId)) ?? false;

  if (isImporting) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        preventCloseClick
        className="confirmation-import"
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: () => setIsImporting(false),
          },
          {
            label: t(translations.modal.confirm),
            type: 'nondestructive',
            onClick: () => onImport(selectedTemplate!.name, selectedTemplate!.id, checkedIds),
            loading: submitting === action,
            disabled:
              checkedIds.length === 0 ||
              !selectedTemplate ||
              (willReplaceActivities &&
                confirmation.toLocaleLowerCase() !==
                  t(translations.activities_type_entry.templates.confirm)),
          },
        ]}
      >
        <Stack className="container">
          <Typography className="title">
            {willReplaceActivities
              ? t(translations.activities_type_entry.templates.confirmation_replace_title, {
                  name: selectedTemplate?.name,
                })
              : t(translations.activities_type_entry.templates.confirmation_title, {
                  name: selectedTemplate?.name,
                })}
          </Typography>
          <Typography className="subtitle">
            {willReplaceActivities
              ? t(translations.activities_type_entry.templates.confirmation_replace_subtitle)
              : t(translations.activities_type_entry.templates.confirmation_subtitle)}
          </Typography>
          {willReplaceActivities && (
            <FlatInput
              required
              value={confirmation}
              onChange={(e) => setConfirmation(e.currentTarget.value)}
            />
          )}
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="templates-modal"
      preventCloseClick
      title={`${t(translations.generic_header.actions[action])}: ${activityName && t(translations.role_permissions[activityName])}`}
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: onClose,
        },
        {
          label: t(translations.modal[action]),
          type: 'nondestructive',
          onClick: () =>
            action === 'save' ? onSave(name, description, checkedIds) : setIsImporting(true),
          loading: submitting === action,
          disabled:
            action === 'save'
              ? checkedIds.length === 0 ||
                !name ||
                !description ||
                !!nameError ||
                !!descriptionError
              : checkedIds.length === 0 || !selectedTemplate,
        },
      ]}
    >
      <Stack className="container">
        {action === 'save' && (
          <FlatInput
            required
            label={t(translations.activities_type_entry.templates.name)}
            counter={{current: name.length, max: MAX_TEMPLATE_NAME_CHARACTERS}}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            errors={nameError}
          />
        )}

        {action === 'import' && (
          <FlatSelectDropdown
            label={t(translations.activities_type_entry.templates.template)}
            placeholder={t(translations.activities_type_entry.templates.select)}
            required
            onChange={(selected) => setSelectedTemplate(templatesIndex![selected.key])}
            options={templates.map((template) => {
              return {key: template.id, label: template.name};
            })}
            value={selectedTemplate && {key: selectedTemplate.id, label: selectedTemplate.name}}
          />
        )}

        {(action === 'save' || (action === 'import' && selectedTemplate)) && (
          <FlatInput
            required
            label={t(translations.activities_type_entry.templates.description)}
            counter={{current: description.length, max: MAX_TEMPLATE_DESCRIPTION_CHARACTERS}}
            value={action === 'save' ? description : selectedTemplate?.description}
            disabled={action === 'import'}
            onChange={(e) => setDescription(e.currentTarget.value)}
            errors={descriptionError}
            rows={3}
            multiline
          />
        )}

        {(action === 'save' || (action === 'import' && selectedTemplate)) && (
          <CollapsibleCheckbox
            label={
              <div className="label-container">
                <Typography>
                  {t(translations.activities_type_entry.templates[`activities_${action}`])}
                  <span> *</span>
                </Typography>
              </div>
            }
            expanded={activitiesExpanded}
            sx={collapsibleCheckboxSx}
            hideHeaderCheck
            toggleExpanded={(value) => setActivitiesExpanded(value)}
          >
            {activities.length > 1 && (
              <NestedCheckbox
                key="all"
                label={t(translations.activities_type_entry.templates.check_all)}
                checked={activitiesIds.every((id) => checkedIds.includes(id))}
                onChange={(checked) => {
                  if (checked) setCheckedIds(activitiesIds);
                  else setCheckedIds([]);
                }}
              />
            )}
            {activities?.map((activity) => {
              return (
                <NestedCheckbox
                  key={activity.id!}
                  label={activity.name ?? ''}
                  checked={checkedIds.includes(activity.id!)}
                  onChange={(checked) => {
                    setCheckedIds((prev) => {
                      if (checked) {
                        return [...prev, activity.id!];
                      }
                      return prev.filter((id) => id !== activity.id);
                    });
                  }}
                />
              );
            })}
          </CollapsibleCheckbox>
        )}
      </Stack>
    </Modal>
  );
};
